import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, MessageCircle } from "lucide-react";
import { FadeUp, SmartImage, Magnetic } from "@/components/motion-primitives";
import { useConsult } from "@/components/ConsultProvider";
import { BLOGS, CONTACT } from "@/data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openConsult } = useConsult();
  const post = BLOGS.find((b) => b.slug === slug);

  useEffect(() => {
    if (!post) navigate("/blog", { replace: true });
  }, [post, navigate]);

  if (!post) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="blog-post-page">
      <section className="relative overflow-hidden bg-navy pb-24 pt-36 text-white">
        <div className="noise-overlay absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-6">
          <FadeUp>
            <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 font-accent text-xs font-semibold uppercase tracking-widest text-gold transition-colors hover:text-gold-soft">
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
            <h1 className="mt-6 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">{post.title}</h1>
            <p className="mt-6 flex items-center gap-2 font-accent text-xs font-semibold uppercase tracking-widest text-slate-300">
              <CalendarDays className="h-4 w-4 text-gold" /> {post.date} · Student First Overseas
            </p>
          </FadeUp>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-6 py-16 md:py-20">
        <FadeUp>
          <SmartImage src={post.img} alt={post.title} className="h-72 w-full rounded-xl border border-line shadow-soft md:h-96" />
        </FadeUp>
        <div className="mt-14 space-y-14">
          {post.sections.map((s, i) => (
            <FadeUp key={i} delay={0.05}>
              <section data-testid={`blog-section-${i}`}>
                {s.heading && (
                  <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">
                    <span className="mr-3 text-gold">{String(i + 1).padStart(2, "0")}</span>{s.heading}
                  </h2>
                )}
                {s.paras?.map((p, j) => (
                  <p key={j} className="mt-5 text-lg leading-relaxed text-muted">{p}</p>
                ))}
                {s.list && (
                  <ol className="mt-6 space-y-4">
                    {s.list.map((item, j) => (
                      <li key={j} className="flex gap-4 rounded-xl border border-line bg-white p-5 shadow-soft">
                        <span className="font-heading text-xl font-extrabold text-gold">{String(j + 1).padStart(2, "0")}</span>
                        <span className="text-base leading-relaxed text-muted">{item}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-20">
          <div className="rounded-xl bg-navy p-10 text-center text-white md:p-14">
            <h3 className="font-heading text-2xl font-extrabold sm:text-3xl">Still have questions?</h3>
            <p className="mx-auto mt-4 max-w-md text-base text-slate-300">
              Book a free consultation — our counselors will map out your exact next steps.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Magnetic>
                <button onClick={() => openConsult()} data-testid="blog-post-consult-button"
                  className="group flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-accent text-sm font-bold uppercase tracking-widest text-navy transition-colors hover:bg-gold-soft">
                  Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </Magnetic>
              <Magnetic>
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="blog-post-whatsapp"
                  className="flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-accent text-sm font-semibold uppercase tracking-widest text-white transition-colors hover:border-gold hover:text-gold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </Magnetic>
            </div>
          </div>
        </FadeUp>
      </article>
    </motion.div>
  );
}
